import { APIProvider, Map } from '@vis.gl/react-google-maps'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { useEffect, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'

import { fetchAdvisers } from '../lib/findAdvisers'
import { fetchOffices } from '../lib/findOffices'
import styles from '../Searchfindcomponent.module.css'
interface NearestViewProps {
  name?: string
  selectedSpecialty?: string
  specitlyOffices?: string[] | null
}

const NearestView: React.FC<NearestViewProps> = ({ selectedSpecialty, specitlyOffices }) => {
  const location =
    process.env.NEXT_PUBLIC_NEXT_LOCATION != null && process.env.NEXT_PUBLIC_NEXT_LOCATION !== ''
      ? process.env.NEXT_PUBLIC_NEXT_LOCATION
      : 'au'
  const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

  const [officeLocations, setOfficeLocations] = useState<
    Array<{ uuid: string; content: Record<string, unknown>; name: string; full_slug: string }>
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null)
  const [loadingAdvisers, setLoadingAdvisers] = useState<boolean>(false)
  const [advisersData, setAdvisersData] = useState<
    Array<{ uuid: string; content: Record<string, unknown>; full_slug: string }>
  >([])
  const [showAdvisers, setShowAdvisers] = useState(false)

  const getOfficeLocations = async () => {
    try {
      setIsLoading(true)
      const officesUUIDs = specitlyOffices?.join(',')
      const { offices } = await fetchOffices({
        officesUUIDs: officesUUIDs != null && officesUUIDs !== '' ? officesUUIDs : undefined,
        location,
        filterLocation,
        paginate: false,
      })
      setOfficeLocations(offices)
    } catch (error) {
      console.error('Error fetching office locations:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setOfficeLocations([])
    setSelectedMarker(null)
    setLoadingAdvisers(false)
    setAdvisersData([])
    setShowAdvisers(false)

    // Only load offices if:
    // 1. No specialty is selected (show all offices), OR
    // 2. Specialty is selected AND we have filtered office UUIDs available
    if (selectedSpecialty === '' || (selectedSpecialty !== '' && specitlyOffices !== null)) {
      getOfficeLocations()
    }
  }, [selectedSpecialty, specitlyOffices])

  useEffect(() => {
    const loadAdvisers = async () => {
      if (selectedMarker != null && selectedMarker !== '') {
        setLoadingAdvisers(true)
        const advisers = await fetchAdvisers(selectedMarker)
        setAdvisersData(advisers)
        setLoadingAdvisers(false)
      } else {
        setAdvisersData([])
      }
    }

    loadAdvisers()
  }, [selectedMarker])

  const renderAdvisers = () => {
    if (loadingAdvisers) {
      return (
        <div className={styles['loadingAdvisers']}>
          <div className={styles['spinner']}></div>
          <p>Loading advisers...</p>
        </div>
      )
    }

    if (!advisersData.length) {
      return (
        <div className={styles['noAdvisers']}>
          <p>No advisers found for this office.</p>
        </div>
      )
    }

    return (
      <ul className={styles['advisersList']}>
        {advisersData.map((advisor) => {
          const { givenName, surname, title } = (advisor.content ?? {}) as {
            givenName?: string
            surname?: string
            title?: string
          }
          const specialties = Array.isArray(advisor.content?.specialities)
            ? (advisor.content.specialities as string[]).join(', ')
            : ''

          return (
            <li key={advisor.uuid} className={styles['adviser']}>
              <a href={`/${advisor.full_slug}`}>
                <div className={styles['adviserImage']}>
                  {(Boolean((advisor.content.filename as string)?.trim()) ||
                    Boolean(
                      (advisor.content.image as { filename: string } | null)?.filename?.trim()
                    )) && (
                    <img
                      src={
                        (advisor.content.filename as string)?.trim() ||
                        ((advisor.content.image as { filename: string } | null)?.filename?.trim() ??
                          '') ||
                        ''
                      }
                      alt={`${givenName ?? ''} ${surname ?? ''}`}
                      width={70}
                      height={70}
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                </div>
                <div className={styles['adviserDetails']}>
                  <h4>
                    {givenName ?? ''} {surname ?? ''}
                  </h4>
                  <p className={styles['adviserTitle']}>{title ?? ''}</p>
                  <p className={styles['adviserSpecialties']}>{specialties}</p>
                </div>
              </a>
            </li>
          )
        })}
      </ul>
    )
  }

  const handleMarkerClick = (officeId: string) => {
    setSelectedMarker(selectedMarker === officeId ? null : officeId)
  }

  let defaultLat = -25.274399
  let defaultLon = 133.775131

  if (process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz') {
    defaultLat = -40.5741727
    defaultLon = 166.0080991
  }

  return (
    <div className={styles.nearestView}>
      {isLoading && <h1 className="sr-only">Loading map...</h1>}

      <div className={styles['mapContainer']}>
        <APIProvider
          apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY as string}
          libraries={['marker']}
        >
          <Map
            mapId="map"
            defaultCenter={{ lat: defaultLat, lng: defaultLon }}
            defaultZoom={4}
            style={{ zIndex: 0 }}
          >
            {officeLocations.map((office) => {
              const isAdvanced = office.content.component === 'advanceOffice'
              const lat = isAdvanced
                ? Number(
                    (office.content.office as { location: { latitude: string }[] }[])?.[0]
                      ?.location?.[0]?.latitude ?? 0
                  )
                : Number(office.content.Latitude as string)
              const lng = isAdvanced
                ? Number(
                    (office.content.office as { location: { longitude: string }[] }[])?.[0]
                      ?.location?.[0]?.longitude ?? 0
                  )
                : Number(office.content.Longitude as string)

              if (isNaN(lat) || isNaN(lng)) return null

              const address = isAdvanced
                ? (office.content.office as { address: string }[])?.[0]?.address
                : [
                    office.content.address,
                    office.content.city,
                    office.content.state,
                    office.content.postcode,
                  ]
                    .filter((part) => part != null && part !== '' && part !== 'null')
                    .join(', ')

              const phone = isAdvanced
                ? (office.content.office as { phone: string }[])?.[0]?.phone
                : (office.content.Phone as string)

              return (
                <div key={office.uuid}>
                  <AdvancedMarker
                    position={{ lat, lng }}
                    onClick={() => handleMarkerClick(office.uuid)}
                  />
                  {selectedMarker === office.uuid && (
                    <InfoWindow
                      position={{ lat, lng }}
                      maxWidth={350}
                      minWidth={350}
                      className={styles['infoWindow']}
                      headerContent={
                        <div className={styles['infoWindowHeader']}>
                          <h1>Findex {office.name}</h1>
                        </div>
                      }
                      onClose={() => setSelectedMarker(null)}
                    >
                      <div className={styles['infoWindowContent']}>
                        <p className={styles['address']}>{address}</p>
                        <p className={styles['phone']}>Ph: {phone}</p>
                        <PhnxButton
                          variant="primary"
                          size="small"
                          label="View Office"
                          type="link"
                          href={`/${office.full_slug}`}
                        />
                      </div>
                      {!showAdvisers && (
                        <PhnxButton
                          variant="quaternary"
                          size="small"
                          label="View Advisers"
                          icon="fx-icon-arrow-down-lg"
                          iconPosition="right"
                          type="button"
                          onClick={() => {
                            setShowAdvisers(!showAdvisers)
                          }}
                        />
                      )}
                      {showAdvisers && renderAdvisers()}
                      {showAdvisers && (
                        <PhnxButton
                          variant="quaternary"
                          size="small"
                          label="Hide Advisers"
                          icon="fx-icon-arrow-up-lg"
                          iconPosition="right"
                          type="button"
                          onClick={() => {
                            setShowAdvisers(!showAdvisers)
                          }}
                        />
                      )}
                    </InfoWindow>
                  )}
                </div>
              )
            })}
          </Map>
        </APIProvider>
      </div>
    </div>
  )
}

export default NearestView
